import { useEffect, useRef, useState } from "react";

/**
 * useScrollReveal
 * Returns a [ref, visible] pair.
 * When the element enters the viewport, `visible` becomes true (once only).
 * Pass `delay` (ms) to stagger reveals.
 */
export function useScrollReveal(delay = 0) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.unobserve(el);
          if (delay > 0) {
            timerRef.current = setTimeout(() => setVisible(true), delay);
          } else {
            setVisible(true);
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [delay]);

  return [ref, visible];
}
